import { formatDate } from '@angular/common';
import {
  Inject,
  InjectionToken,
  LOCALE_ID,
  Optional,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { DateTime } from 'luxon';

export const DATE_PIPE_DEFAULT_TIMEZONE = new InjectionToken<string>(
  'DATE_PIPE_DEFAULT_TIMEZONE'
);

@Pipe({
  name: 'date',
})
export class DatePipe implements PipeTransform {
  constructor(
    @Inject(LOCALE_ID) private locale: string,
    @Inject(DATE_PIPE_DEFAULT_TIMEZONE)
    @Optional()
    private defaultTimezone?: string | null
  ) {}

  transform(
    value: Date | string | number,
    format?: string,
    timezone?: string,
    locale?: string
  ): string | null;
  transform(
    value: null | undefined,
    format?: string,
    timezone?: string,
    locale?: string
  ): null;
  transform(
    value: Date | string | number | null | undefined,
    format?: string,
    timezone?: string,
    locale?: string
  ): string | null;
  transform(
    value: Date | string | number | null | undefined,
    format = 'mediumDate',
    timezone?: string,
    locale?: string
  ): string | null {
    if (value == null || value === '' || value !== value) return null;

    try {
      if (globalThis.sigv.appContext?.envConfig?.format?.date === format) {
        return this._formatDateWithoutTimezone(value, format);
      }
      const dateStr = formatDate(
        value,
        format,
        locale || this.locale,
        timezone ?? this.defaultTimezone ?? undefined
      );
      return dateStr;
    } catch {
      return null;
    }
  }

  private _formatDateWithoutTimezone(
    value: Date | string | number,
    format: string
  ): string | null {
    let y = '',
      m = '',
      d = '',
      h = '00',
      mi = '00',
      s = '00';

    switch (typeof value) {
      case 'string': {
        // timestamp 字串處理
        if (/^\d+$/.test(value)) {
          return this._formatDateWithoutTimezone(
            new Date(Number(value)),
            format
          );
        }
        // 標準日期字串處理
        // 支援 '1979-05-09', '1979/05/09', '1979-05-09T01:00:00+09:00'
        const pure = value.split('T')[0].split('+')[0].split(' ')[0];
        const match = pure.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})$/);
        if (match) {
          y = match[1];
          m = match[2];
          d = match[3];
        } else {
          // 嘗試轉化為日期
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            y = date.getFullYear().toString();
            m = (date.getMonth() + 1).toString().padStart(2, '0');
            d = date.getDate().toString().padStart(2, '0');
            h = date.getHours().toString().padStart(2, '0');
            mi = date.getMinutes().toString().padStart(2, '0');
            s = date.getSeconds().toString().padStart(2, '0');
          } else {
            return null;
          }
        }
        break;
      }
      case 'number': {
        // 當成 timestamp
        return this._formatDateWithoutTimezone(new Date(value), format);
      }
      default: {
        // format 為 Date 格式時, 比照 sigv-input-date-picker updateCalendarValue 處理, 轉換為 本地時間
        if (value instanceof Date) {
          value = this.convertTimezoneToLocal(value, true);

          y = value.getFullYear().toString();
          m = (value.getMonth() + 1).toString().padStart(2, '0');
          d = value.getDate().toString().padStart(2, '0');
          h = value.getHours().toString().padStart(2, '0');
          mi = value.getMinutes().toString().padStart(2, '0');
          s = value.getSeconds().toString().padStart(2, '0');
        } else {
          return null;
        }
      }
    }

    let result = format;
    result = result
      .replace(/yyyy/g, y)
      .replace(/MM/g, m)
      .replace(/dd/g, d)
      .replace(/HH/g, h)
      .replace(/mm/g, mi)
      .replace(/ss/g, s);

    return result;
  }

  // 有循環引用問題, 暫解, 先複製
  private convertTimezoneToLocal(date: Date, reverse: boolean): Date {
    if (this.isNullOrEmpty(date)) {
      return date;
    }
    const reverseValue = reverse ? -1 : 1;
    const dateTimezoneOffset = date.getTimezoneOffset();
    const timezoneOffset = 0 - DateTime.local().offset;
    const localDate = new Date(date.getTime());
    localDate.setMinutes(
      localDate.getMinutes() +
        reverseValue * (timezoneOffset - dateTimezoneOffset)
    );
    return localDate;
  }

  // 有循環引用問題, 暫解, 先複製
  private isNullOrEmpty(value: any): boolean {
    return (
      typeof value === 'undefined' ||
      value == null ||
      (typeof value === 'string' && value === '')
    );
  }
}
